/**
 * Trainer activity feed.
 *
 * Reads GET /v1/activity — the ai_training endpoint that assembles a
 * per-trainer timeline out of ExamPaper events (upload / ready /
 * failed / processing) plus a 30-day daily trend and headline totals.
 *
 * Layout, top → bottom:
 *   1. Greeting + last-activity meta
 *   2. KPI strip (Uploads / Ready / Failed / In progress)
 *   3. 30-day upload trend — inline SVG bar chart, no dep
 *   4. Event timeline grouped by day, per-event icon + status pill,
 *      speciality / level / module / unit path per row
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import {
  Activity as ActivityIcon, AlertCircle, AlertTriangle, ArrowRight,
  CheckCircle2, Clock, FileText, Layers, Loader2, PackageOpen,
  RefreshCcw, TrendingUp, Upload,
} from 'lucide-react';
import { aiTrainerClient } from '@/lib/aiTrainerClient';
import { useAiTrainerAuth } from '@/contexts/AiTrainerAuthContext';

/* ────────────────────────────────────────────────────────────────── */
/* Types                                                              */
/* ────────────────────────────────────────────────────────────────── */
type EventKind = 'uploaded' | 'ready' | 'failed' | 'processing';

interface Event {
  kind:        EventKind;
  paper_id:    string;
  file_name:   string;
  course_code?: string | null;
  speciality?:  string | null;
  level?:       string | null;
  module?:      string | null;
  unit?:        string | null;
  at?:          string | null;
  detail?: {
    chunks?: number;
    pages?:  number;
    embedding_model?: string;
    error?: string;
    stage?: string;
  } | null;
}

interface TrendPoint {
  date:    string;
  uploads: number;
  ready:   number;
  failed:  number;
}

interface ActivityData {
  totals: {
    uploads:     number;
    ready:       number;
    failed:      number;
    in_progress: number;
    chunks:      number;
    pages:       number;
  };
  trend:        TrendPoint[];
  events:       Event[];
  last_upload?: string | null;
  last_ready?:  string | null;
  generated_at?: string;
  window_days?: number;
}

/* ────────────────────────────────────────────────────────────────── */
/* Helpers                                                            */
/* ────────────────────────────────────────────────────────────────── */
const BRAND  = '#1a5276';
const ACCENT = '#0e9172';
const GOLD   = '#b07d2b';
const PURP   = '#6d4aa6';
const DANGER = '#c0392b';

const fmt = (n: number | null | undefined) =>
  n == null ? '—' : new Intl.NumberFormat().format(n);

const fmtDate = (iso?: string | null) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
};

const fmtDayLabel = (iso: string) => {
  try {
    return new Date(iso + 'T00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  } catch { return iso; }
};

const fmtRel = (iso?: string | null) => {
  if (!iso) return 'never';
  const t = new Date(iso).getTime();
  if (isNaN(t)) return '—';
  const d = (Date.now() - t) / 1000;
  if (d < 60)         return 'just now';
  if (d < 3600)       return `${Math.round(d / 60)} min ago`;
  if (d < 86_400)     return `${Math.round(d / 3600)} h ago`;
  if (d < 86_400 * 7) return `${Math.round(d / 86_400)} d ago`;
  return fmtDate(iso);
};

const KIND_META: Record<EventKind, { label: string; icon: typeof CheckCircle2; tone: string; bg: string; fg: string; ring: string }> = {
  uploaded:   { label: 'Uploaded',       icon: Upload,        tone: BRAND,  bg: '#1a52760f', fg: BRAND,  ring: '#1a527622' },
  ready:      { label: 'Ready',          icon: CheckCircle2,  tone: ACCENT, bg: '#0e91720f', fg: ACCENT, ring: '#0e917222' },
  failed:     { label: 'Failed',         icon: AlertCircle,   tone: DANGER, bg: '#c0392b0f', fg: DANGER, ring: '#c0392b22' },
  processing: { label: 'Processing',     icon: Loader2,       tone: GOLD,   bg: '#b07d2b0f', fg: GOLD,   ring: '#b07d2b22' },
};

const explainError = (e: unknown): string => {
  const ax = e as AxiosError<{ detail?: string; message?: string }>;
  if (ax?.response) {
    const url    = ax.config?.url ?? '?';
    const status = ax.response.status;
    const body   = ax.response.data as { detail?: string; message?: string };
    return `${status} on ${url}${body?.detail ? ` — ${body.detail}` : body?.message ? ` — ${body.message}` : ''}`;
  }
  return ax?.message || 'Request failed';
};

/* ────────────────────────────────────────────────────────────────── */
/* Page                                                               */
/* ────────────────────────────────────────────────────────────────── */
export default function ActivityPage() {
  const { user }              = useAiTrainerAuth();
  const [data, setData]       = useState<ActivityData | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState<string | null>(null);
  const [tick, setTick]       = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await aiTrainerClient.get('/activity?days=30&limit=100');
      setData(r.data?.data ?? r.data);
    } catch (e) {
      setErr(explainError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, tick]);

  const totals    = data?.totals;
  const trend     = data?.trend ?? [];
  const events    = data?.events ?? [];

  /* Group events by day (YYYY-MM-DD) for the timeline */
  const grouped = useMemo(() => {
    const m = new Map<string, Event[]>();
    events.forEach((e) => {
      const key = (e.at || '').slice(0, 10) || 'unknown';
      const arr = m.get(key) ?? [];
      arr.push(e);
      m.set(key, arr);
    });
    return [...m.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [events]);

  return (
    <div
      className="min-h-screen bg-slate-50 px-4 pb-16 pt-8 sm:px-6 lg:px-8"
      style={{ fontFamily: "'Afacad Flux', system-ui, sans-serif" }}
    >
      <div className="mx-auto max-w-6xl">
        {/* Head */}
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
              Corpus · Activity
            </div>
            <h1
              className="mt-1 text-3xl leading-tight text-slate-900 sm:text-4xl"
              style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic' }}
            >
              Hello{' '}
              <span className="text-[#1a5276]">
                {user?.full_name?.split(' ')[0] || 'trainer'}
              </span>
              <span className="not-italic">.</span>
            </h1>
            <p className="mt-1 max-w-xl text-sm text-slate-600">
              Every upload you've queued and how far each one has moved through
              the ingestion pipeline.
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            {data?.last_upload && (
              <span className="inline-flex items-center gap-1.5">
                <Clock size={13} />
                Last upload {fmtRel(data.last_upload)}
              </span>
            )}
            <button
              onClick={() => setTick((t) => t + 1)}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:border-[#1a5276] hover:text-[#1a5276]"
            >
              <RefreshCcw size={13} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>

        {err && (
          <div className="mt-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="font-semibold">Activity feed unavailable.</div>
              <div className="mt-1 break-all font-mono text-xs">{err}</div>
            </div>
            <button
              onClick={() => setTick((t) => t + 1)}
              className="shrink-0 text-xs font-semibold underline hover:no-underline"
            >
              Retry
            </button>
          </div>
        )}

        {/* KPI strip */}
        <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <KpiCard tone="brand"  icon={PackageOpen}  label="Uploads"
                   value={fmt(totals?.uploads)} loading={loading}
                   sub={totals ? <>Across {fmt(totals.pages)} pages</> : '—'} />
          <KpiCard tone="accent" icon={CheckCircle2} label="Ready"
                   value={fmt(totals?.ready)} loading={loading}
                   sub={totals ? <>{fmt(totals.chunks)} embedded chunks</> : '—'} />
          <KpiCard tone="gold"   icon={ActivityIcon} label="In progress"
                   value={fmt(totals?.in_progress)} loading={loading}
                   sub="Ingestion running" />
          <KpiCard tone="danger" icon={AlertCircle}  label="Failed"
                   value={fmt(totals?.failed)} loading={loading}
                   sub={totals?.failed ? 'Reprocess from Library' : '—'} />
        </div>

        {/* Trend chart */}
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Last {data?.window_days ?? 30} days
              </div>
              <h3
                className="mt-1 text-xl text-slate-900"
                style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic' }}
              >
                Daily uploads
              </h3>
              <p className="mt-1 text-xs text-slate-500">
                Stacked bars — green ready, gold still processing, red failed.
              </p>
            </div>
            {totals && totals.uploads > 0 && (
              <div className="inline-flex items-center gap-1 text-sm font-semibold text-[#0e9172]">
                <TrendingUp size={14} />
                {Math.round((totals.ready / Math.max(totals.uploads, 1)) * 100)}% ready
              </div>
            )}
          </div>
          <div className="mt-4">
            <TrendChart data={trend} />
          </div>
        </div>

        {/* Timeline */}
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
          <div className="border-b border-slate-100 px-5 py-4">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              Timeline
            </div>
            <h3
              className="mt-1 text-xl text-slate-900"
              style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic' }}
            >
              Recent events
            </h3>
          </div>

          {loading && !data ? (
            <TimelineSkeleton />
          ) : grouped.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="divide-y divide-slate-100">
              {grouped.map(([day, dayEvents]) => (
                <DayGroup key={day} day={day} events={dayEvents} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/* Chart                                                              */
/* ────────────────────────────────────────────────────────────────── */
function TrendChart({ data }: { data: TrendPoint[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const H = 140;                       // svg height
  const barW = 100 / Math.max(data.length, 1);
  const maxU = Math.max(1, ...data.map((d) => d.uploads));

  if (data.length === 0) {
    return (
      <div className="grid h-[140px] place-items-center text-sm text-slate-400">
        No data yet.
      </div>
    );
  }

  return (
    <div className="relative">
      <svg viewBox={`0 0 100 ${H}`} preserveAspectRatio="none" className="h-[160px] w-full">
        {/* horizontal grid lines at 25/50/75/100% of max */}
        {[0.25, 0.5, 0.75, 1].map((frac) => (
          <line
            key={frac}
            x1={0} x2={100}
            y1={H - frac * (H - 20)} y2={H - frac * (H - 20)}
            stroke="#e2e8f0" strokeWidth={0.2}
          />
        ))}
        {data.map((d, i) => {
          const x = i * barW + barW * 0.15;
          const w = barW * 0.7;
          const uH = ((d.uploads) / maxU) * (H - 20);
          const rH = ((d.ready)   / maxU) * (H - 20);
          const fH = ((d.failed)  / maxU) * (H - 20);
          const inflight = Math.max(0, uH - rH - fH);
          let y = H - uH;
          const segs = [
            { h: rH,       fill: ACCENT },
            { h: inflight, fill: GOLD   },
            { h: fH,       fill: DANGER },
          ];
          return (
            <g
              key={d.date}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              style={{ cursor: 'default' }}
            >
              {segs.map((s, si) => {
                if (s.h <= 0) return null;
                const rect = (
                  <rect
                    key={si} x={x} y={y} width={w} height={s.h}
                    fill={s.fill}
                    opacity={hover === null || hover === i ? 1 : 0.4}
                    rx={si === 0 && rH === uH ? 1 : 0}
                  />
                );
                y += s.h;
                return rect;
              })}
              {/* hit target for the whole column */}
              <rect x={i * barW} y={0} width={barW} height={H} fill="transparent" />
            </g>
          );
        })}
      </svg>

      {/* Hover tooltip */}
      {hover !== null && data[hover] && (
        <div
          className="pointer-events-none absolute -translate-x-1/2 -translate-y-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] shadow-lg"
          style={{ left: `${(hover + 0.5) * (100 / data.length)}%`, top: -6 }}
        >
          <div className="font-bold text-slate-900">{fmtDayLabel(data[hover].date)}</div>
          <div className="text-[#0e9172]">
            Ready {data[hover].ready}
          </div>
          <div className="text-[#b07d2b]">
            Processing {Math.max(0, data[hover].uploads - data[hover].ready - data[hover].failed)}
          </div>
          <div className="text-[#c0392b]">
            Failed {data[hover].failed}
          </div>
        </div>
      )}

      {/* X-axis: 5 sparse labels */}
      <div className="mt-2 flex justify-between text-[10px] font-semibold uppercase tracking-wider text-slate-400">
        {[0, Math.floor(data.length * 0.25), Math.floor(data.length * 0.5), Math.floor(data.length * 0.75), data.length - 1]
          .filter((v, i, a) => a.indexOf(v) === i)
          .map((i) => (
            <span key={i}>{new Date(data[i].date + 'T00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
          ))}
      </div>

      {/* Legend */}
      <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-slate-600">
        <LegendChip color={ACCENT} label="Ready" />
        <LegendChip color={GOLD}   label="Processing" />
        <LegendChip color={DANGER} label="Failed" />
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/* Timeline                                                           */
/* ────────────────────────────────────────────────────────────────── */
function DayGroup({ day, events }: { day: string; events: Event[] }) {
  const isToday = day === new Date().toISOString().slice(0, 10);
  const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = day === yesterday.toISOString().slice(0, 10);
  const heading = isToday ? 'Today' : isYesterday ? 'Yesterday' : fmtDayLabel(day);

  return (
    <div className="px-5 py-4">
      <div className="mb-3 flex items-center gap-2">
        <span
          className="text-[11px] font-extrabold uppercase tracking-[.14em] text-slate-500"
          style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}
        >
          {heading}
        </span>
        <span className="h-px flex-1 bg-slate-100" />
        <span className="text-[11px] text-slate-400">
          {events.length} event{events.length === 1 ? '' : 's'}
        </span>
      </div>

      <ol className="space-y-2">
        {events.map((e, i) => (
          <EventRow key={`${e.paper_id}-${e.kind}-${i}`} event={e} />
        ))}
      </ol>
    </div>
  );
}

function EventRow({ event }: { event: Event }) {
  const meta = KIND_META[event.kind];
  const Icon = meta.icon;
  const path = [event.speciality, event.level, event.module, event.unit].filter(Boolean).join(' › ');

  return (
    <li
      className="grid grid-cols-[36px_1fr_auto] items-center gap-3 rounded-xl border border-slate-100 bg-white px-3 py-2.5 transition hover:border-slate-200 hover:shadow-[0_2px_10px_rgba(15,23,35,0.04)]"
    >
      <span
        className="flex h-9 w-9 items-center justify-center rounded-xl"
        style={{ background: meta.bg, color: meta.fg, boxShadow: `inset 0 0 0 1px ${meta.ring}` }}
      >
        <Icon size={14} className={event.kind === 'processing' ? 'animate-spin' : ''} />
      </span>

      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span
            className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider"
            style={{ background: meta.bg, color: meta.fg }}
          >
            {meta.label}
          </span>
          <span className="truncate text-[13px] font-semibold text-slate-900">
            {event.course_code || event.file_name}
          </span>
        </div>
        {path && (
          <div className="mt-0.5 truncate text-[11.5px] text-slate-500">
            {path}
          </div>
        )}
        {event.kind === 'ready' && event.detail?.chunks != null && (
          <div className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-slate-500">
            <Layers size={10} />
            {event.detail.chunks} chunk{event.detail.chunks === 1 ? '' : 's'}
            {event.detail.pages ? ` · ${event.detail.pages} page${event.detail.pages === 1 ? '' : 's'}` : ''}
            {event.detail.embedding_model ? ` · ${event.detail.embedding_model}` : ''}
          </div>
        )}
        {event.kind === 'processing' && event.detail?.stage && (
          <div className="mt-0.5 text-[11px] capitalize text-[#b07d2b]">
            Stage: {event.detail.stage}…
          </div>
        )}
        {event.kind === 'failed' && event.detail?.error && (
          <div className="mt-0.5 truncate text-[11px] text-[#c0392b]" title={event.detail.error}>
            {event.detail.error}
          </div>
        )}
      </div>

      <div className="flex flex-col items-end gap-0.5 text-right">
        <span className="text-[11px] text-slate-500">{fmtRel(event.at)}</span>
        <span className="text-[10.5px] text-slate-400">
          {event.file_name.length > 22 ? `${event.file_name.slice(0, 22)}…` : event.file_name}
        </span>
      </div>
    </li>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/* Presentational helpers                                             */
/* ────────────────────────────────────────────────────────────────── */
type Tone = 'brand' | 'accent' | 'gold' | 'purp' | 'danger';
const TONE_BG: Record<Tone, string> = {
  brand:  '#1a527612',
  accent: '#0e917212',
  gold:   '#b07d2b12',
  purp:   '#6d4aa612',
  danger: '#c0392b12',
};
const TONE_FG: Record<Tone, string> = {
  brand: BRAND, accent: ACCENT, gold: GOLD, purp: PURP, danger: DANGER,
};

function KpiCard({
  tone, icon: Icon, label, value, sub, loading,
}: {
  tone: Tone;
  icon: typeof CheckCircle2;
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  loading?: boolean;
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
      <span
        aria-hidden
        className="absolute -right-6 -top-6 h-20 w-20 rounded-full"
        style={{ background: TONE_BG[tone] }}
      />
      <div className="relative">
        <div
          className="inline-flex rounded-xl p-2"
          style={{ background: TONE_BG[tone], color: TONE_FG[tone] }}
        >
          <Icon size={16} />
        </div>
        <div className="mt-3 text-[10.5px] font-semibold uppercase tracking-wider text-slate-400">
          {label}
        </div>
        <div
          className="mt-1 text-3xl leading-none text-slate-900"
          style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic' }}
        >
          {loading
            ? <span className="inline-block h-7 w-14 animate-pulse rounded bg-slate-100" />
            : value}
        </div>
        {sub != null && <div className="mt-2 text-xs text-slate-500">{sub}</div>}
      </div>
    </div>
  );
}

function LegendChip({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: color }} />
      {label}
    </span>
  );
}

function EmptyState() {
  return (
    <div className="p-12 text-center">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#1a5276]/10 to-[#0e9172]/10 text-[#0e9172]">
        <FileText size={20} />
      </div>
      <p className="mt-3 text-[13px] font-semibold text-slate-700">Nothing here yet</p>
      <p className="mx-auto mt-1 max-w-sm text-[12px] text-slate-500">
        Head over to the Library and upload your first past paper — every
        upload, retry, and ingestion event will land here.
      </p>
      <a
        href="/library"
        className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#1a5276] px-3.5 py-2 text-[12px] font-bold text-white shadow-[0_6px_16px_rgba(26,82,118,0.25)] hover:bg-[#15436a]"
      >
        Go to Library <ArrowRight size={12} />
      </a>
    </div>
  );
}

function TimelineSkeleton() {
  return (
    <div className="space-y-4 px-5 py-6">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="space-y-2">
          <div className="h-3 w-20 animate-pulse rounded bg-slate-100" />
          {Array.from({ length: 2 }).map((_, j) => (
            <div key={j} className="grid grid-cols-[36px_1fr_auto] items-center gap-3 rounded-xl border border-slate-100 p-3">
              <div className="h-9 w-9 animate-pulse rounded-xl bg-slate-100" />
              <div className="space-y-2">
                <div className="h-3 w-2/3 animate-pulse rounded bg-slate-100" />
                <div className="h-2.5 w-1/2 animate-pulse rounded bg-slate-100" />
              </div>
              <div className="h-3 w-16 animate-pulse rounded bg-slate-100" />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
